import { useParams } from 'react-router-dom';

import { useAssetStalenessQuery, useCohortProfileQuery } from '@verantos-dev/data-access-cohorts';

import { useRedirectOnInvalidParams } from '../../hooks/initialization/useRedirectOnInvalidParams';
import { useCohortHydration } from '../hydration/useCohortHydration';

/**
 * Orchestration hook for the gen-3 cohort detail page. Pure TanStack +
 * Zustand, all imports from inside `gen-3/`:
 *
 * - `useRedirectOnInvalidParams` (kept from v0; pure routing) bounces
 *   bad URLs.
 * - `useCohortHydration` fires the gen-3 TanStack reads (cohort
 *   version + concept groups) and hydrates the Zustand
 *   cohort-definition store. Replaces the legacy
 *   `useCohortBuilderPage` chain that brought RTK Query and Redux.
 * - `useCohortProfileQuery` (gen-3-owned, same endpoint as the shared
 *   AV hook but a separate cache entry) supplies the cohort name +
 *   owner the header renders.
 *
 * Loading is split by what each consumer actually reads, not OR'd into one
 * flag — the header (name/owner/last-modified) only needs the profile
 * query, Study period only needs the definition query, and Criteria needs
 * both definition and concept groups (it resolves concept-group names).
 * Collapsing all three into one flag would hold every section back by
 * whichever of the three calls is slowest, even sections that don't
 * depend on it.
 */
export interface UseProjectCohortDetailPageParams {
  /** TODO: cleanup with asset_edit_while_in_use ff — read once by the
   *  top-level `ProjectCohortDetailPage` and passed down so this hook
   *  doesn't re-check the flag itself. */
  isAssetEditWhileInUseEnabled?: boolean;
}

export const useProjectCohortDetailPage = ({
  isAssetEditWhileInUseEnabled = false,
}: UseProjectCohortDetailPageParams = {}) => {
  const { projectId, cohortId, cohortVersionId } = useParams<{
    projectId: string;
    cohortId: string;
    cohortVersionId: string;
  }>();

  const safeProjectId = projectId ?? '';
  const safeCohortId = cohortId ?? '';
  const safeCohortVersionId = cohortVersionId ?? '';

  useRedirectOnInvalidParams();

  const {
    isLoading: isHydrationLoading,
    isDefinitionLoading,
    isError: isHydrationError,
  } = useCohortHydration({
    projectId: safeProjectId,
    cohortId: safeCohortId,
    cohortVersionId: safeCohortVersionId,
  });

  const {
    data: cohortProfile,
    isLoading: isProfileQueryLoading,
    isError: isProfileError,
  } = useCohortProfileQuery({
    projectId: safeProjectId,
    cohortId: safeCohortId,
  });

  // Read-only banner data from the generic asset-staleness
  // endpoint. Not folded into the Zustand definition store since staleness
  // is display-only, not editable state. Gated on the caller-supplied flag
  // so the query doesn't fire at all while the flag is off.
  // TODO: cleanup with asset_edit_while_in_use ff
  const { data: staleness } = useAssetStalenessQuery({
    kind: 'cohort',
    id: safeCohortVersionId,
    enabled: isAssetEditWhileInUseEnabled,
  });

  return {
    projectId: safeProjectId,
    cohortId: safeCohortId,
    cohortVersionId: safeCohortVersionId,
    cohortProfile,
    // Header: name/owner/last-modified come only from the profile query.
    isProfileLoading: !!isProfileQueryLoading,
    // Study period: reads only the definition query.
    isDefinitionLoading: !!isDefinitionLoading,
    // Criteria: resolves concept-group names, so it needs both.
    isHydrationLoading: !!isHydrationLoading,
    isError: !!(isHydrationError || isProfileError),
    showStaleAnalyticalVariablesWarning:
      isAssetEditWhileInUseEnabled && (staleness?.isStale ?? false),
  };
};
